import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  CircularProgress,
} from '@mui/material';
import { authService } from '../services/authService';
import { resolveActivePlatform } from '../utils/resolveActivePlatform';
import { tokens } from '../theme/tokens';

function activePlatformLabel() {
  try {
    const raw = authService.getStoredUser();
    const p = resolveActivePlatform(raw || {});
    if (p === 'youtube') return 'YouTube';
    if (p === 'facebook') return 'Facebook';
    return 'Reddit';
  } catch {
    return 'this platform';
  }
}

/**
 * Two explicit logout paths: session-only vs purge stored scan data for the active platform.
 */
export default function LogoutActions({ variant = 'navbar' }) {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeError, setPurgeError] = useState(null);

  const isDashboard = variant === 'dashboard';

  const handlePlainLogout = async () => {
    try {
      await authService.logout();
      navigate('/login');
    } catch {
      navigate('/login');
    }
  };

  const handlePurgeConfirm = async () => {
    setPurgeError(null);
    setPurging(true);
    try {
      await authService.logoutAndDeleteSavedData();
      navigate('/login');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Could not delete saved data.';
      setPurgeError(msg);
    } finally {
      setPurging(false);
    }
  };

  const outlinedPrimarySx = isDashboard
    ? {
        minHeight: 40,
        color: tokens.text.primary,
        borderColor: tokens.border.strong,
        '&:hover': {
          borderColor: tokens.accent.cyan,
          backgroundColor: 'rgba(34, 211, 238, 0.08)',
        },
      }
    : {
        borderRadius: 2,
        borderWidth: 1.5,
        fontWeight: 600,
      };

  const outlinedWarningSx = isDashboard
    ? {
        minHeight: 40,
        color: tokens.accent.warning,
        borderColor: 'rgba(245, 158, 11, 0.45)',
        '&:hover': {
          borderColor: tokens.accent.warning,
          backgroundColor: 'rgba(245, 158, 11, 0.12)',
        },
      }
    : {
        borderRadius: 2,
        borderWidth: 1.5,
        fontWeight: 600,
      };

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button variant="outlined" color="primary" onClick={handlePlainLogout} sx={outlinedPrimarySx}>
          Logout
        </Button>
        <Button variant="outlined" color="warning" onClick={() => setDialogOpen(true)} sx={outlinedWarningSx}>
          Logout &amp; delete saved data
        </Button>
      </Stack>

      <Dialog open={dialogOpen} onClose={() => !purging && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Delete saved data and log out?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            This removes SafeSocial&apos;s stored posts, comments, exposure findings, and ad insights for{' '}
            <strong>{activePlatformLabel()}</strong> only. Other platforms you may have linked are unchanged.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: purgeError ? 1 : 0 }}>
            Your {activePlatformLabel()} connection tokens in SafeSocial will also be removed; you will need to sign in
            again to run a new scan.
          </Typography>
          {purgeError ? (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {purgeError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={purging}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" onClick={handlePurgeConfirm} disabled={purging}>
            {purging ? <CircularProgress size={22} color="inherit" /> : 'Delete data & log out'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
